import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@context/AuthContext'
import type { SecurityPortalKey } from '@/types'
import './OtpVerificationNotice.css'

interface Props {
  portal: SecurityPortalKey
  profilePath: string
  className?: string
}

interface CopyConfig {
  heading: string
  body: string
  cta: string
}

const copyByPortal: Record<SecurityPortalKey, CopyConfig> = {
  customer: {
    heading: 'Verify your email to protect your account',
    body: 'Email verification keeps your order updates and password resets secure. Complete the one-time check to avoid sign-in interruptions.',
    cta: 'Review in profile'
  },
  retail: {
    heading: 'Verify your email to protect your account',
    body: 'Email verification keeps your order updates and password resets secure. Complete the one-time check to avoid sign-in interruptions.',
    cta: 'Review in profile'
  },
  seller: {
    heading: 'Verify your seller email',
    body: 'We need a verified email to deliver onboarding decisions, payout alerts, and OTP codes. Finish verification to keep your store live.',
    cta: 'Manage verification'
  },
  delivery: {
    heading: 'Verify your delivery email',
    body: 'Verified email ensures dispatch assignments and OTP codes reach you quickly. Complete verification to prevent access issues.',
    cta: 'Review now'
  },
  operations: {
    heading: 'Verify your operations email',
    body: 'A verified email keeps workflow escalations and secure logins running smoothly. Finish verification to stay on top of queues.',
    cta: 'Review in profile'
  },
  admin: {
    heading: 'Verify your admin email',
    body: 'Verified email is required for sensitive finance and admin notifications. Complete verification to maintain access.',
    cta: 'Manage verification'
  }
}

const statusLabel: Record<string, string> = {
  PENDING: 'Pending',
  VERIFIED: 'Verified',
  UNVERIFIED: 'Not verified'
}

function formatStatus(value: string | null | undefined): string {
  if (!value) return 'Not verified'
  return statusLabel[value] ?? value.toLowerCase()
}

export default function OtpVerificationNotice({ portal, profilePath, className }: Props) {
  const { authState, refreshOtpStatus } = useAuth()
  const [hydrated, setHydrated] = useState(Boolean(authState.otpEmailStatus))
  const [isRefreshing, setIsRefreshing] = useState(false)
  const storageKey = useMemo(() => `otp_notice_dismissed_${portal}`, [portal])
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false
    }
    return window.sessionStorage.getItem(storageKey) === 'true'
  })

  useEffect(() => {
    if (!authState.isAuthenticated || hydrated) {
      return
    }
    setIsRefreshing(true)
    refreshOtpStatus()
      .catch(() => {
        // Swallow refresh errors; banner will still render with fallback status
      })
      .finally(() => {
        setIsRefreshing(false)
        setHydrated(true)
      })
  }, [authState.isAuthenticated, hydrated, refreshOtpStatus])

  const emailStatus = authState.otpEmailStatus ?? null
  const hasChannels = (authState.otpChannels?.length ?? 0) > 0
  const shouldShow = authState.isAuthenticated && !authState.pendingMfa && (emailStatus !== 'VERIFIED' || !hasChannels) && !dismissed

  const copy = useMemo(() => copyByPortal[portal] ?? copyByPortal.retail, [portal])

  if (!shouldShow) {
    return null
  }

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true)
      await refreshOtpStatus()
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleDismiss = () => {
    setDismissed(true)
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(storageKey, 'true')
    }
  }

  const rootClass = ['otp-notice', className].filter(Boolean).join(' ')
  const statusClass = `otp-notice__status otp-notice__status--${(emailStatus ?? 'UNVERIFIED').toLowerCase()}`

  const bodyCopy = hasChannels
    ? copy.body
    : 'No verified contact channel is configured. Please reach out to your administrator or support to enable OTP verification.'

  return (
    <aside className={rootClass} role="status" aria-live="polite">
      <div className="otp-notice__icon" aria-hidden="true">🔒</div>
      <div className="otp-notice__content">
        <div className="otp-notice__heading">
          <strong>{copy.heading}</strong>
          <span className={statusClass}>
            {formatStatus(emailStatus)}
          </span>
        </div>
        <p>{bodyCopy}</p>
      </div>
      <div className="otp-notice__actions">
        <Link to={profilePath} className="otp-notice__cta">
          {copy.cta}
        </Link>
        <button type="button" className="otp-notice__refresh" onClick={handleRefresh} disabled={isRefreshing}>
          {isRefreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        <button type="button" className="otp-notice__dismiss" onClick={handleDismiss}>
          Dismiss
        </button>
      </div>
    </aside>
  )
}
